/**
 * The BE product/variation payloads carry the admin's "Short Description"
 * field as `short_description` (raw payloads: school-shop variations, menu-item
 * and collection listings) or `shortDescription` (after `adaptVariation`).
 * It is the subtitle a ProductCard shows next to / under its title and the
 * eyebrow line above the title on the product page.
 *
 * The field is optional and very often an empty string, so this normalises
 * "nothing to show" to `undefined` — ProductCard only renders the description
 * (and, on CLS, its bullet separator) when it is truthy.
 *
 * It is also hidden when it only repeats what the page shows anyway
 * (2026-10-22, CLS SE "Studentoverall": the admin's short description was the
 * description's first lines, later the product name): same text as the name,
 * or the beginning of the description.
 *
 * @param {object} item — a BE product, variation or package payload
 * @returns {string|undefined} the trimmed short description, or undefined
 */
export default function productShortDescription(item) {
  const raw = item?.short_description ?? item?.shortDescription;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const key = normalise(trimmed);
  if (key && key === normalise(item?.name)) return undefined;
  const description = normalise(item?.description);
  if (key && description && description.startsWith(key)) return undefined;

  return trimmed;
}

// Case, whitespace and trailing punctuation do not make two texts different.
function normalise(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/[\s ]+/g, ' ')
    .replace(/^[\s\-–•.:!?]+|[\s\-–•.:!?]+$/g, '')
    .trim()
    .toLowerCase();
}
